<template>
    <div>
        <div v-if="!loading && !resettingList">
            <!-- Group type header -->
            <div class="flex items-center justify-between gap-x-6 mb-2" v-if="commonIssues && commonIssues.length > 0">
                <span class="text-md text-gray-800 font-normal flex items-center gap-x-1">
                    <span>{{ companyType | titlecase }} Issue Options</span>

                    <button @click="toggleMaintenanceRequestGroupDropdown(companyType)"
                            class="text-gray-400 transition duration-300 hover:text-indigo-600 outline-none">
                        <icon-chevron-down class="h-6 w-6 transform transition duration-300"
                                           :class="dropdown ? 'rotate-180' : ''">
                        </icon-chevron-down>
                    </button>
                </span>

                <div class="flex items-center gap-4">
                    <toggle v-model="isAvailable"
                            active-color-bg-class="bg-green-600"
                            active-color-text-class="text-green-600"
                            @input="toggleMaintenanceRequestGroupForClients(companyType)">
                        List available to clients
                    </toggle>

                    <secondary-button @click.prevent="toggleShowCreateEditModal()">
                        New Issue
                    </secondary-button>

                    <secondary-button @click.prevent="toggleShowResetConfirmation()">
                        Reset To Defaults
                    </secondary-button>
                </div>
            </div>

            <transition name="slide">
                <div v-show="dropdown">
                    <div class="w-full rounded-md border-t border-gray-100 shadow">
                        <div class="rounded-t-md stick-below-nav bg-white">
                            <div class="flex items-center font-medium rounded-t-md ">
                                <div class="w-2/12 py-3 px-2">Order</div>
                                <div class="w-3/12 py-3">Category</div>
                                <div class="w-3/12 py-3">Location</div>
                                <div class="w-3/12 py-3">Display Label</div>
                                <div class="w-1/12"></div>
                            </div>
                        </div>

                        <draggable :list="commonIssues" class="w-full"
                                   :options="{animation: 100, handle: '.drag-handle'}" :element="'div'"
                                   @change="reorderCommonMaintenanceRequestIssue">
                            <div v-for="(issue, index) in commonIssues"
                                 class="flex items-center font-normal drag-handle rounded-sm odd:bg-gray-100" :key="index">
                                <div class="w-2/12 py-3 px-2 border-none">#{{ issue.order + 1 }}</div>
                                <div class="w-3/12 py-3 border-none">{{ issue.category  | titlecase }}</div>
                                <div class="w-3/12 py-3 border-none">{{ issue.location | titlecase }}</div>
                                <div class="w-3/12 py-3 border-none">{{ issue.label | titlecase }}</div>
                                <div class="w-1/12 py-3 px-2 flex justify-end items-center gap-x-2 border-none">
                                    <button @click="toggleShowCreateEditModal(issue)" :title="'Edit issue #' + issue.order + 1"
                                            class="text-gray-400 hover:text-indigo-600 transition duration-300 outline-none">
                                        <icon-pencil-alt class="w-4 h-4"></icon-pencil-alt>
                                    </button>

                                    <button @click="removeIssue(issue)" :title="'Remove issue #' + issue.order + 1"
                                            :disabled="removingIssue"
                                            class="text-gray-400 hover:text-brand transition duration-300 outline-none">
                                        <icon-x-circle class="w-4 h-4"></icon-x-circle>
                                    </button>
                                </div>
                            </div>
                        </draggable>
                    </div>
                </div>
            </transition>
        </div>

        <div v-if="loading || resettingList" class="flex justify-center py-48 items-center">
            <icon-refresh class="h-10 w-10 text-indigo-600 animate-spin"/>
        </div>

        <confirmation-dialog :show="showResetConfirmationModal"
                             :proceed-button-text="resettingList ? 'Resetting...' : 'Reset List'"
                             :proceed-disabled="resettingList"
                             :show-cancel-button="!resettingList"
                             :show-icon="false"
                             @cancelled="toggleShowResetConfirmation()"
                             @proceed="resetList()"
                             title="Reset Default Maintenance Request Issues"
                             proceed-button-colour="indigo-600"
                             proceed-button-hover-colour="indigo-400">
            <template slot="body">
                <div>
                    Are you sure that you want to reset to the default
                    <span class="font-medium">{{ companyType }}</span> maintenance request issues? This will reset all custom data and ordering.
                </div>
            </template>
        </confirmation-dialog>

        <common-maintenance-request-issues-create-edit-modal :issue="selectedIssue" :show="showCreateEditModal"
                                                             :company-type="companyType"
                                                             @edited-issue="replaceIssue" @added-issue="addIssue"
                                                             @close="toggleShowCreateEditModal(null)">
        </common-maintenance-request-issues-create-edit-modal>
    </div>
</template>

<script>
import MaintenanceRequestSettingsMixin from "../../mixins/maintenance-request-settings-mixin";
import CommonMaintenanceRequestIssuesCreateEditModal from "./CommonMaintenanceRequestIssuesCreateEditModal";

export default {

    components: {CommonMaintenanceRequestIssuesCreateEditModal},

    mixins: [MaintenanceRequestSettingsMixin],

    props: {
        companyType: {
            type: String,
            required: true,
            description: "The group of issues to display"
        },
        settingsId: {
            type: Number,
            required: true,
            description: "Used to load the correct team's common maintenance issues"
        },
    },

    data () {
        return {
            loading: true,
            commonIssues: [],
            selectedIssue: null,
            dropdown: false,
            isAvailable: false,
            showResetConfirmationModal: false,
            showCreateEditModal: false,
            resettingList: false,
            removingIssue: false,
            allCommonMaintenanceRequestCategories: [
                "air-conditioning",
                "appliances",
                "doors and locks",
                "electricity",
                "garden and pool",
                "garden and yard",
                "gates and fencing",
                "plumbing",
                "miscellaneous",
                "other",
                "security",
                "structural",
                "walls and fencing",
                "warehouse",
            ]
        };
    },

    mounted () {
        this.fetchCommonIssues();
    },

    methods: {
        fetchCommonIssues (resetting = false) {
            this.loading = true;
            axios.get(`/settings/maintenance/requests/${this.settingsId}/get-common-issues`, {
                params: {
                    company_type: this.companyType
                }
            })
            .then(response => {
                this.commonIssues = response.data;

                if (this.commonIssues && this.commonIssues.length > 0) {
                    this.isAvailable = !!this.commonIssues[0].is_available;
                    this.dropdown = !!resetting;
                }

                this.loading = false;
            }).catch(error => {
                this.loading = false;
                toastr.error(error.message);
            });

        },

        toggleMaintenanceRequestGroupForClients (companyType) {
            App.patch(`/settings/maintenance/requests/${this.settingsId}/toggle-list`, new AppForm({company_type: companyType}))
            .then(response => {
                toastr.success(response);
            })
            .catch(error => {
                toastr.error(error.message);
            });
        },

        toggleShowResetConfirmation () {
            this.showResetConfirmationModal = !this.showResetConfirmationModal;
        },

        reorderCommonMaintenanceRequestIssue (dragEvent) {
            if (dragEvent.moved.newIndex !== dragEvent.moved.oldIndex) {
                this.setIssueOrderFromIndex();

                App.patch(`/settings/maintenance/requests/${this.settingsId}/reorder-issues`, new AppForm({
                    list: this.commonIssues.map(issue => {
                        return {id: issue.id, order: issue.order};
                    }),
                    company_type: this.companyType
                }))
                .catch(error => {
                    toastr.error(error.message);
                });
            }
        },

        removeIssue (issue) {
            this.removingIssue = true;
            App.delete(`/settings/maintenance/requests/remove-issue/${issue.id}`, new AppForm({}))
            .then(response => {
                let index = this.commonIssues.findIndex(i => i.id === issue.id);
                if (index !== -1) {
                    this.commonIssues.splice(index, 1);
                    // Remove list completely or reorder list
                    if (this.commonIssues.length === 0) {
                        this.$emit("change-company-types", this.companyType);
                    } else {
                        this.setIssueOrderFromIndex();
                    }
                }
                toastr.success(response);
                this.removingIssue = false;
            })
            .catch(error => {
                this.removingIssue = false;
                toastr.error(error.message);
            });
        },

        resetList () {
            this.resetMaintenanceRequestGroupToDefaults(this.settingsId, this.companyType, true)
            .then(() => this.fetchCommonIssues(true));
        },

        setIssueOrderFromIndex () {
            this.commonIssues.forEach((issue, index) => {
                this.$set(issue, "order", index);
            });
        },

        toggleMaintenanceRequestGroupDropdown () {
            this.dropdown = !this.dropdown;
        },

        toggleShowCreateEditModal (issue) {
            this.selectedIssue = issue;
            this.showCreateEditModal = !this.showCreateEditModal;
        },

        replaceIssue (issue) {
            let index = this.commonIssues.findIndex(i => i.id === issue.id);
            if (index !== -1) {
                this.commonIssues.splice(index, 1, issue);
            }
        },

        addIssue (issue) {
            this.commonIssues.push(issue);
            this.dropdown = true;
        },
    },
};
</script>

<style>
.stick-below-nav {
    position: sticky !important;
    top: 78px !important;
}
</style>
